/**
 * @packageDocumentation
 * @module hooks/useResizeObserver
 */
import { useCallback, useEffect, useRef } from 'react';

/**
 * Returns an instance of `ResizeObserver`. If you aren't sure how to use this,
 * you may want to look at `useResizeObserverRef` instead.
 *
 * @param onResize the function to call when a resize is detected.
 *
 * @see useResizeObserverRef
 * @see https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 */
export function useResizeObserver(onResize) {
  const refOnResize = useRef(onResize);
  useEffect(() => {
    refOnResize.current = onResize;
  });
  const refResizeObserver = useRef();
  useEffect(
  // cleanup on component dismount
  () => () => {
    // ignoring calling disconnect as each resizeObserver instance only observes one element in our case which
    // is tracked and unobserved in the useResizeObserverRef hook.
    refResizeObserver.current = undefined;
  }, []);
  if (refResizeObserver.current === undefined) {
    // NOTE: creating this in render is fine, but if users try to call observe on this during first render,
    //     | it can leak event handlers and the resize observer instance in concurrent mode.
    refResizeObserver.current = new ResizeObserver(function __onResize(entries, observer) {
      const callback = refOnResize.current;
      callback(entries, observer);
    });
  }
  return refResizeObserver.current;
}

/**
 * Creates a DOM ref that has a resize observer attached.
 *
 * @param onResize the function to call when a resize is detected
 * @param options options to pass to the ResizeObserver
 *
 * @returns A tuple with a function ref and a getter for the current ref value.
 *
 * @example
 * ```jsx
 * function MyC() {
 *   const [ref] = useResizeObserverRef((entry) => {
 *     console.log(`resized: ${entry.borderBoxSize[0].inlineSize}`);
 *   });
 *
 *   return <div ref={ref} />;
 * }
 * ```
 */
export function useResizeObserverRef(onResize, options) {
  const resizeObserver = useResizeObserver(function ([entry]) {
    setTimeout(() => {
      if (ref.current) {
        onResize(entry);
      }
    }, 0);
  });
  const box = options?.box;
  const ref = useRef(null);
  return [useCallback(instance => {
    if (instance) {
      if (instance !== ref.current && ref.current) {
        resizeObserver.unobserve(ref.current);
      }
      ref.current = instance;
      resizeObserver.observe(ref.current, {
        box
      });
    } else {
      if (ref.current) {
        resizeObserver.unobserve(ref.current);
      }
      ref.current = null;
    }
  }, [resizeObserver, box]), useCallback(() => ref.current, [])];
}
//# sourceMappingURL=useResizeObserver.js.map